import type { Config } from 'tailwindcss'
import tailwindAnimate from 'tailwindcss-animate'

// We want each package to be responsible for its own content.
const config: Omit<Config, 'content'> = {
  safelist: [
    'col-span-1',
    'col-span-2',
    'col-span-3',
    'col-span-4',
    'col-span-5',
    'col-span-6',
    'col-span-7',
    'col-span-8',
    'col-span-9',
    'col-span-10',
    'col-span-11',
    'col-span-12',
    'md:col-span-1',
    'md:col-span-2',
    'md:col-span-3',
    'md:col-span-4',
    'md:col-span-5',
    'md:col-span-6',
    'md:col-span-7',
    'md:col-span-8',
    'md:col-span-9',
    'md:col-span-10',
    'md:col-span-11',
    'md:col-span-12',
    'lg:col-span-1',
    'lg:col-span-2',
    'lg:col-span-3',
    'lg:col-span-4',
    'lg:col-span-5',
    'lg:col-span-6',
    'lg:col-span-7',
    'lg:col-span-8',
    'lg:col-span-9',
    'lg:col-span-10',
    'lg:col-span-11',
    'lg:col-span-12',
    {
      pattern: /grid-cols-./,
    },
  ],
  variants: {
    extend: {
      borderColor: ['focus', 'primary'],
      ringWidth: ['focus'],
      ringColor: ['focus'],
      shadow: ['focus'],
      padding: ['focus'],
      backgroundColor: ['focus'],
      background: ['focus'],
    },
  },
  theme: {
    extend: {
      fontSize: {
        '2xs': '0.625rem',
        '3xs': '0.5rem',
      },
      colors: {
        border: 'hsl(var(--border))',
        input: 'hsl(var(--input))',
        ring: 'hsl(var(--ring))',
        background: 'hsl(var(--background))',
        body: {
          background: 'hsl(var(--body-background))',
        },
        sidebar: {
          background: 'hsl(var(--sideBar-background))',
        },
        foreground: 'hsl(var(--foreground))',
        brand: {
          dark: 'var(--brand-dark)',
        },
        primary: {
          DEFAULT: 'hsl(var(--primary))',
          foreground: 'hsl(var(--primary-foreground))',
        },
        secondary: {
          DEFAULT: 'hsl(var(--secondary))',
          foreground: 'hsl(var(--secondary-foreground))',
        },
        destructive: {
          DEFAULT: 'hsl(var(--destructive))',
          foreground: 'hsl(var(--destructive-foreground))',
        },
        muted: {
          DEFAULT: 'hsl(var(--muted))',
          foreground: 'hsl(var(--muted-foreground))',
        },
        accent: {
          DEFAULT: 'hsl(var(--accent))',
          foreground: 'hsl(var(--accent-foreground))',
        },
        'mobile-nav-background': 'var(--mobile-nav-background)',
        'message-background-gradient': 'var(--message-background-gradient)',
        'message-background': 'var(--message-background)',
        'message-mine': {
          DEFAULT: 'hsl(var(--message-mine))',
          foreground: 'hsl(var(--message-mine-foreground))',
        },
        composer: {
          DEFAULT: 'hsl(var(--composer-background))',
          border: 'hsl(var(--composer-border))',
        },
        popover: {
          DEFAULT: 'hsl(var(--popover))',
          foreground: 'hsl(var(--popover-foreground))',
        },
        card: {
          DEFAULT: 'hsl(var(--card))',
          foreground: 'hsl(var(--card-foreground))',
        },
        hover: 'hsl(var(--hover))',
      },
      borderRadius: {
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 4px)',
      },
      boxShadow: {
        composer: 'var(--composer-shadow)',
      },
      keyframes: {
        'caret-blink': {
          '0%,70%,100%': { opacity: '1' },
          '20%,50%': { opacity: '0' },
        },
        'slide-down': {
          from: {
            transform: 'translateY(-4px)',
            opacity: '0',
            height: '0',
          },
          to: {
            transform: 'translateY(0)',
            opacity: '1',
            height: 'var(--radix-collapsible-content-height)',
          },
        },
        'slide-up': {
          from: {
            transform: 'translateY(0)',
            opacity: '1',
            height: 'var(--radix-collapsible-content-height)',
          },
          to: {
            transform: 'translateY(-4px)',
            opacity: '0',
            height: '0',
          },
        },
        'nav-content-in': {
          from: {
            opacity: '0',
            transform: 'translateY(6px)',
          },
          to: {
            opacity: '1',
            transform: 'translateY(0)',
          },
        },
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' },
        },
        'accordion-down-bounce': {
          '0%': { height: '0' },
          '70%': {
            height: 'calc(var(--radix-accordion-content-height) * 1.05)',
          },
          '100%': { height: 'var(--radix-accordion-content-height)' },
        },
        'bounce-in': {
          '0%': {
            opacity: '0',
            transform: 'scale(0.3)',
          },
          '50%': {
            opacity: '0.7',
            transform: 'scale(1.1)',
          },
          '80%': {
            opacity: '0.9',
            transform: 'scale(0.89)',
          },
          '100%': {
            opacity: '1',
            transform: 'scale(1)',
          },
        },
        'slide-in': {
          '0%': {
            transform: 'translateX(-10px)',
            opacity: '0',
          },
          '100%': {
            transform: 'translateX(0)',
            opacity: '1',
          },
        },
        'slide-out': {
          '0%': {
            transform: 'translateX(0)',
            opacity: '1',
          },
          '100%': {
            transform: 'translateX(-10px)',
            opacity: '0',
          },
        },
        'rainbow-border': {
          '0%': { backgroundPosition: '0% 50%' },
          '100%': { backgroundPosition: '400% 50%' },
        },
      },
      animation: {
        'caret-blink': 'caret-blink 1.25s ease-out infinite',
        'slide-down': 'slide-down 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
        'slide-up': 'slide-up 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        'nav-content-in': 'nav-content-in 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'accordion-down-bounce':
          'accordion-down-bounce 0.25s cubic-bezier(.2,1.12,.6,1.5)',
        'bounce-in': 'bounce-in 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        'slide-in': 'slide-in 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        'slide-out': 'slide-out 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        'rainbow-border': 'rainbow-border 12s linear infinite',
      },
      transitionTimingFunction: {
        bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      },
    },
  },
  plugins: [tailwindAnimate],
}
export default config
